import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Typography,
  OutlinedInput,
  Checkbox,
  ListItemText,
} from '@mui/material';
import adminActions from '../../../../actions/adminActions';

function getSelectedPermitTypeNames(selectedPermitTypes, permitTypes) {
  return selectedPermitTypes.map((selectedPermitType) => {
    const permitType = permitTypes.find((permitType) => permitType.id === selectedPermitType);
    if (permitType) {
      return permitType.name;
    }
    return '';
  });
}

function getSelectedTerritoryNames(selectedTerritories, territories) {
  return selectedTerritories.map((selectedTerritory) => {
    const territory = territories.find((territory) => territory.id === selectedTerritory);
    if (territory) {
      return territory.name;
    }
    return '';
  });
}

const CombinationSelector = ({ reviewGroupTerritories, permitTypes, user }) => {
  const dispatch = useDispatch();
  const [selectedPermitTypes, setSelectedPermitTypes] = useState([]);
  const [selectedTerritories, setSelectedTerritories] = useState([]);

  const handleSelectAllPermitTypes = (event) => {
    if (event.target.checked) {
      setSelectedPermitTypes(permitTypes.map((type) => type.id));
    } else {
      setSelectedPermitTypes([]);
    }
  };

  const handleSelectAllTerritories = (event) => {
    if (event.target.checked) {
      setSelectedTerritories(reviewGroupTerritories.territories.map((territory) => territory.id));
    } else {
      setSelectedTerritories([]);
    }
  };

  const addCombinations = (user) => {
    if (selectedPermitTypes.length > 0 && selectedTerritories.length > 0) {
      const combinations = [];
      selectedPermitTypes.forEach((permitTypeId) => {
        selectedTerritories.forEach((territoryId) => {
          combinations.push({ permitTypeId, territoryId });
        });
      });

      // don't add duplicate combinations
      const existingCombinations = new Set(
        user.assignedPermitTypeLocations.map((combo) => JSON.stringify(combo))
      );
      const newCombinations = combinations.filter(
        (combo) => !existingCombinations.has(JSON.stringify(combo))
      );

      dispatch(adminActions.assignPermitTypeLocations(user.id, newCombinations));
      setSelectedPermitTypes([]);
      setSelectedTerritories([]);
    }
  };

  return (
    <Box>
      <Typography variant="h6">Select Combinations</Typography>
      <FormControl fullWidth sx={{ mt: 2 }}>
        <InputLabel>Permit Types</InputLabel>
        <Select
          multiple
          value={selectedPermitTypes}
          onChange={(e) => setSelectedPermitTypes(e.target.value)}
          input={<OutlinedInput label="Permit Types" />}
          renderValue={(selected) => getSelectedPermitTypeNames(selected, permitTypes).join(', ')}
        >
          <MenuItem value="select-all">
            <Checkbox
              checked={selectedPermitTypes.length === permitTypes.length}
              indeterminate={
                selectedPermitTypes.length > 0 && selectedPermitTypes.length < permitTypes.length
              }
              onChange={handleSelectAllPermitTypes}
            />
            <ListItemText primary="Select All" />
          </MenuItem>
          {permitTypes.map((type) => (
            <MenuItem key={type.id} value={type.id}>
              <Checkbox checked={selectedPermitTypes.indexOf(type.id) > -1} />
              <ListItemText primary={type.name} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl fullWidth sx={{ mt: 2 }}>
        <InputLabel>Territories</InputLabel>
        <Select
          multiple
          value={selectedTerritories}
          onChange={(e) => {
            if (e.target.value.includes('select-all')) {
              e.preventDefault();
              return;
            }
            setSelectedTerritories(e.target.value);
          }}
          input={<OutlinedInput label="Territories" />}
          renderValue={(selected) =>
            getSelectedTerritoryNames(selected, reviewGroupTerritories.territories).join(', ')
          }
        >
          <MenuItem value="select-all">
            <Checkbox
              checked={selectedTerritories.length === reviewGroupTerritories.territories.length}
              indeterminate={
                selectedTerritories.length > 0 &&
                selectedTerritories.length < reviewGroupTerritories.territories.length
              }
              onChange={handleSelectAllTerritories}
            />
            <ListItemText primary="Select All" />
          </MenuItem>
          {reviewGroupTerritories.territories.map((territory) => (
            <MenuItem key={territory.id} value={territory.id}>
              <Checkbox checked={selectedTerritories.indexOf(territory.id) > -1} />
              <ListItemText primary={territory.name} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Button
        variant="contained"
        color="primary"
        fullWidth
        sx={{ mt: 2 }}
        disabled={selectedPermitTypes.length === 0 || selectedTerritories.length === 0}
        onClick={() => addCombinations(user)}
      >
        Add Combinations
      </Button>
    </Box>
  );
};

CombinationSelector.propTypes = {
  user: PropTypes.object.isRequired,
  reviewGroupTerritories: PropTypes.shape({
    reviewGroupName: PropTypes.string,
    reviewGroupValue: PropTypes.string,
    territories: PropTypes.array,
  }).isRequired,
  permitTypes: PropTypes.array.isRequired,
};

export default CombinationSelector;
